import { useEffect, useState } from "react";
import { Download, ExternalLink, Maximize2, Minimize2, RefreshCw } from "lucide-react";
import { Button, Badge } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import { resolvePreviewKind } from "@/design-system/lib/preview";

export type PreviewState =
  | "ready"
  | "loading"
  | "scanning"
  | "generating"
  | "notgenerated"
  | "failed"
  | "error"
  | "unavailable";

export interface PreviewDocument {
  title: string;
  fileName?: string | null;
  contentType?: string | null;
  url?: string | null;
  state?: PreviewState;
  message?: string | null;
  markdownFallback?: React.ReactNode;
  onRetry?: () => void;
  onDownload?: () => void;
  downloadDisabled?: boolean;
  // Staging recovery — ISO timestamp the current generation (re-)started, so
  // the panel can tick an elapsed-time label while Generating.
  generatingSince?: string | null;
  // Staging recovery — manual regenerate for a stuck/failed assessment. Shown
  // while Generating and on the Failed state; disabled during the cooldown.
  onRegenerate?: () => void;
  regenerateDisabled?: boolean;
  // Authoritative signal from the backend preview endpoint that `url` resolves
  // to a PDF (native, or a server-converted Office file). When set it overrides
  // the contentType/fileName heuristic so converted Office docs render inline.
  isPdf?: boolean | null;
}

/** Ticks roughly every 15s so a Generating label stays current without a
 *  heavy timer. Returns e.g. "2m" / "45s", or null when no start is known. */
function useElapsedLabel(since: string | null | undefined, active: boolean): string | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active || !since) return;
    const t = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(t);
  }, [active, since]);
  if (!since) return null;
  const startedMs = new Date(since).getTime();
  if (Number.isNaN(startedMs)) return null;
  const seconds = Math.max(0, Math.floor((now - startedMs) / 1000));
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m`;
}

interface DocumentPreviewPaneProps {
  document: PreviewDocument;
  expanded?: boolean;
  onToggleExpand?: () => void;
  onOpened?: () => void;
}

export function DocumentPreviewToolbar({
  document,
  expanded,
  onToggleExpand,
}: DocumentPreviewPaneProps) {
  const elapsed = useElapsedLabel(document.generatingSince, document.state === "generating");
  return (
    <div className="flex flex-wrap items-center justify-between gap-space-2 border-b border-border-light p-space-3">
      <div className="min-w-0">
        <h3 className="m-0 truncate text-small font-semibold text-onyx">{document.title}</h3>
        {document.fileName && (
          <p className="m-0 truncate text-caption text-gray-mid">{document.fileName}</p>
        )}
      </div>
      <div className="flex items-center gap-space-2">
        {document.state === "scanning" && <Badge status="info">Scanning</Badge>}
        {document.state === "generating" && (
          <Badge status="info">{elapsed ? `Generating… ${elapsed}` : "Generating"}</Badge>
        )}
        {document.state === "notgenerated" && <Badge status="archived">Not generated</Badge>}
        {document.state === "failed" && <Badge status="error">Failed</Badge>}
        {document.onRegenerate &&
          (document.state === "generating" ||
            document.state === "notgenerated" ||
            document.state === "failed" ||
            document.state === "unavailable") && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={document.regenerateDisabled}
              onClick={document.onRegenerate}
            >
              <RefreshCw size={14} className="mr-1 inline" /> Regenerate
            </Button>
          )}
        {document.onRetry && (document.state === "error" || document.state === "unavailable") && (
          <Button type="button" variant="secondary" size="sm" onClick={document.onRetry}>
            <RefreshCw size={14} className="mr-1 inline" /> Retry
          </Button>
        )}
        {document.onDownload && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={document.downloadDisabled}
            onClick={document.onDownload}
          >
            <Download size={14} className="mr-1 inline" /> Download
          </Button>
        )}
        {onToggleExpand && (
          <Button type="button" variant="text" size="sm" onClick={onToggleExpand}>
            {expanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            <span className="sr-only">{expanded ? "Restore pane" : "Expand pane"}</span>
          </Button>
        )}
      </div>
    </div>
  );
}

export function DocumentPreviewPane({ document, expanded, onToggleExpand, onOpened }: DocumentPreviewPaneProps) {
  const fileName = document.fileName ?? "document";
  const kind = resolvePreviewKind({
    contentType: document.contentType,
    fileName,
    isPdf: document.isPdf,
  });
  const isPdf = kind === "pdf";
  const isImage = kind === "image";
  const state = document.state ?? (document.url ? "ready" : "unavailable");

  return (
    <section className="flex min-h-[theme(spacing.80)] flex-col overflow-hidden rounded-md border border-border-light bg-white-pure">
      <DocumentPreviewToolbar document={document} expanded={expanded} onToggleExpand={onToggleExpand} />
      <div className="flex flex-1 flex-col bg-paper-warm p-space-3">
        {state === "loading" && <StateMessage message="Preparing preview…" />}
        {state === "scanning" && <StateMessage message={document.message ?? "Virus scan is still in progress."} />}
        {state === "generating" && <StateMessage message={document.message ?? "Assessment document is generating."} />}
        {state === "notgenerated" && <StateMessage message={document.message ?? "AI assessment has not been generated yet."} />}
        {state === "failed" && (
          <StateMessage
            tone="error"
            message={document.message ?? "Assessment generation failed. Use Regenerate to try again."}
          />
        )}
        {state === "error" && <StateMessage tone="error" message={document.message ?? "Preview failed."} />}
        {state === "unavailable" && (
          document.markdownFallback ? (
            <div className="rounded border border-border-light bg-white-pure p-space-3 text-small text-onyx">
              {document.markdownFallback}
            </div>
          ) : (
            <StateMessage message={document.message ?? "Preview unavailable."} />
          )
        )}
        {state === "ready" && document.url && isPdf && (
          <iframe
            src={document.url}
            title={`Preview of ${fileName}`}
            onLoad={onOpened}
            className="min-h-[theme(spacing.96)] flex-1 rounded border border-border-light bg-white-pure"
          />
        )}
        {state === "ready" && document.url && isImage && (
          <div className="flex flex-1 items-start justify-center rounded border border-border-light bg-white-pure p-space-2">
            <img src={document.url} alt={`Preview of ${fileName}`} onLoad={onOpened} className="max-h-full w-auto" />
          </div>
        )}
        {state === "ready" && document.url && !isPdf && !isImage && (
          <div className="flex flex-col items-start gap-space-2 rounded border border-border-light bg-white-pure p-space-4 text-small text-gray-mid">
            <span>Inline preview is not available for this file type.</span>
            <a href={document.url} target="_blank" rel="noreferrer" onClick={onOpened} className="inline-flex items-center font-semibold text-onyx hover:text-gold-shadow">
              <ExternalLink size={14} className="mr-1 inline" /> Open in new tab
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

function StateMessage({ message, tone = "muted" }: { message: string; tone?: "muted" | "error" }) {
  return (
    <div className={cn("rounded border p-space-4 text-small", tone === "error" ? "border-status-error bg-status-error/10 text-status-error" : "border-border-light bg-white-pure text-gray-mid")}>
      {message}
    </div>
  );
}

interface DocumentComparisonPanelProps {
  left: PreviewDocument;
  right: PreviewDocument;
  onLeftOpened?: () => void;
  onRightOpened?: () => void;
}

export function DocumentComparisonPanel({ left, right, onLeftOpened, onRightOpened }: DocumentComparisonPanelProps) {
  const [expanded, setExpanded] = useState<"left" | "right" | null>(null);
  return (
    <div className="grid grid-cols-1 gap-space-4 xl:grid-cols-2" data-testid="document-comparison-panel">
      {(expanded === null || expanded === "left") && (
        <div className={expanded === "left" ? "xl:col-span-2" : undefined}>
          <DocumentPreviewPane document={left} expanded={expanded === "left"} onToggleExpand={() => setExpanded(expanded === "left" ? null : "left")} onOpened={onLeftOpened} />
        </div>
      )}
      {(expanded === null || expanded === "right") && (
        <div className={expanded === "right" ? "xl:col-span-2" : undefined}>
          <DocumentPreviewPane document={right} expanded={expanded === "right"} onToggleExpand={() => setExpanded(expanded === "right" ? null : "right")} onOpened={onRightOpened} />
        </div>
      )}
    </div>
  );
}
